import { ReactNode, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { usePathname, useSearchParams } from "next/navigation";
import { parse } from "qs";

import { listingSortDisplayStrings } from "@/constants/dispensaries/filters";
import { Category } from "@/constants/events";
import { Query } from "@/custom-types/Query";
import { StoresV1Pagination } from "@/custom-types/StoreLocator";
import { useEventTracker } from "@/hooks/useEventTracker";
import toggleURLSearchParamValue from "@/utils/toggleURLSearchParamValue";

import FilterShortcut from "@/components/botanic/FilterShortcut";
import DoctorAd from "@/components/DoctorAd";
import { AvailableFilter } from "@/components/DynamicFilters/types";
import { FilterButton } from "@/components/FilteredGrid/FilterButton";
import CloseIcon from "@/components/Icons/x.svg";
import AppliedChips from "@/components/UnifiedShop/AppliedChips";

import { useFilterQueryParams } from "../../MenuFilters/v2/hooks";
import SortDropdown from "../SortDropdown/SortDropdown";

// This isn't on the page until the Filter panel is open
// so we can dynamically import it
const DynamicFilters = dynamic(
  () => import("../../DynamicFilters/DynamicFilters"),
  {
    loading: () => <div className="p-lg">Loading filters...</div>,
    ssr: false,
  },
);

const StoreFilters = ({
  children,
  availableFilters,
  filtersOpen,
  setFiltersOpen,
  pagination,
  availableSorts,
  retailType,
}: {
  children: ReactNode;
  availableFilters: AvailableFilter[];
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  loading?: boolean;
  pagination: StoresV1Pagination;
  availableSorts: string[];
  retailType: string;
}) => {
  const paginatedOverflowContainerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const el = paginatedOverflowContainerRef.current;
    if (!el) {
      return;
    }

    // Next.js only restores window scroll.
    // When content is inside an overflow-y-auto container, we must
    // manually reset the container scroll position on navigation.
    el.scrollTop = 0;
  }, [pathname, searchParams]);

  const [openMobileSectionKey, setOpenMobileSectionKey] = useState<string>();
  const { publishEvent } = useEventTracker();
  const {
    actions: { clearAllFilters },
  } = useFilterQueryParams();
  const query = parse(searchParams?.toString() || "");
  const filtersApplied = query.filter as Query;
  const numberOfFiltersSelected = Object.entries(filtersApplied || {}).length;

  const onShortcutClick = (filterSectionKey?: string) => {
    setOpenMobileSectionKey(filterSectionKey);
    setFiltersOpen(true);
  };

  const handleTracking = (filterLabel?: string) => {
    publishEvent({
      action: "click",
      category: `Finder: ${retailType} Quick Filter`,
      label: filterLabel,
    });
  };

  return (
    <>
      {!filtersOpen ? (
        <div
          className={classNames(
            "flex flex-col h-full min-h-0 gap-sm md:gap-0 md:pt-lg",
          )}
          data-testid={"filter-shortcut-view-container"}
        >
          <div
            data-testid="filter-container"
            className={classNames(
              "flex items-center whitespace-nowrap md:pb-sm md:mb-xs gap-sm overflow-x-auto overflow-y-hidden px-lg max-sm:no-scrollbar",
              { "flex-wrap": numberOfFiltersSelected },
            )}
          >
            <FilterButton
              filterCount={numberOfFiltersSelected}
              onClick={() => {
                setFiltersOpen(true);
              }}
              className="!text-default"
            />
            {availableFilters.length > 0 &&
              !filtersApplied &&
              availableFilters.map((filter) => (
                <FilterShortcut
                  key={filter.name}
                  className="!text-default"
                  showChevron={filter.type !== "toggle"}
                  text={filter.label}
                  href={
                    filter.type === "toggle"
                      ? {
                          pathname,
                          query: toggleURLSearchParamValue(
                            searchParams,
                            `filter[${filter.name}]`,
                            "true",
                          ).toString(),
                        }
                      : undefined
                  }
                  handleTracking={() => {
                    handleTracking(filter.label);
                  }}
                  onClick={() => {
                    onShortcutClick(filter.name);
                    handleTracking(filter.label);
                  }}
                />
              ))}
            <AppliedChips
              availableFilters={availableFilters}
              filterQueryStringKey={(qsKey) => `filter[${qsKey}]`}
            />
          </div>
          {pagination.currentCount > 0 && (
            <div className="flex justify-between items-start px-lg">
              <div className="flex items-start">
                <div
                  className="flex-shrink-0 text-xs mr-xxl"
                  data-testid="listing-result-count"
                >
                  <span className="font-bold">
                    {`Results ${pagination.currentCount > 0 ? pagination.skip + 1 : 0}-${pagination.skip + pagination.currentCount}`}
                  </span>
                  {` of ${pagination.totalCount}`}
                </div>
              </div>

              <SortDropdown
                availableSorts={availableSorts}
                eventCategory={Category.finderSort}
                sortDisplayStrings={listingSortDisplayStrings}
                sortsAsLinks={true}
              />
            </div>
          )}
          <div
            ref={paginatedOverflowContainerRef}
            data-testid="paginated-overflow-container"
            className="flex-1 overflow-y-auto flex flex-col gap-xxl *:px-lg py-lg"
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            {retailType === "clinic" && (
              <div className="[&_div]:m-0">
                <DoctorAd />
              </div>
            )}
            {children}
          </div>
        </div>
      ) : (
        <div
          className={classNames("flex flex-col h-full min-h-0 bg-white")}
          data-testid={"filter-view-container"}
        >
          <div className="flex justify-between items-center p-3.5">
            <div className="title text-xs uppercase font-extrabold">
              Filters
            </div>
            <button
              aria-label="Close modal"
              className="close-button"
              onClick={() => {
                setFiltersOpen(false);
              }}
              data-testid="close-modal"
            >
              <CloseIcon width={22} height={22} />
            </button>
          </div>
          <div className="flex items-center whitespace-nowrap pb-sm mb-xs gap-xs no-scrollbar overflow-x-auto overflow-y-hidden flex-wrap px-lg">
            <AppliedChips
              availableFilters={availableFilters}
              filterQueryStringKey={(qsKey) => `filter[${qsKey}]`}
            />
          </div>
          <div
            className="flex-1 overflow-y-auto no-scrollbar"
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DynamicFilters
              availableFilters={availableFilters}
              defaultOpenKey={openMobileSectionKey}
              sectionPadding="px-lg"
              // add `nofollow` when the new url will have more than 1 filter
              noFollowLink={(href) =>
                decodeURI(href.query).lastIndexOf("&") > -1
              }
              eventCategory={`Finder: ${retailType} Filter`}
              includeFilterEvents
              filterQueryStringKey={(filterName) => `filter[${filterName}]`}
            />
          </div>
          <div className="sticky bottom-0 flex w-full mt-auto p-lg bg-white border-t border-light-grey">
            <button
              onClick={clearAllFilters}
              className="button button--text flex-shrink-0 text-sm text-default"
            >
              clear all
            </button>
            <button
              className="button w-[252px] ml-auto"
              onClick={() => {
                setFiltersOpen(false);
              }}
            >
              view results
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default StoreFilters;
